import { useEffect, useCallback, useMemo } from 'react';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import { ActiveSelection } from 'fabric';
import { useDesignStore } from '@/stores';
import {
  useCanvasStateManager,
  useCanUndo,
  useCanRedo,
} from '@/stores/canvasStateManager';
import { debounce } from '@/lib/utils';

/**
 * Keyboard shortcut definitions
 */
const SHORTCUTS = {
  'ctrl+z': 'undo',
  'ctrl+shift+z': 'redo',
  'ctrl+y': 'redo',
  'ctrl+s': 'save',
  'ctrl+d': 'duplicate',
  delete: 'deleteSelected',
  backspace: 'deleteSelected',
  escape: 'deselect',
  'ctrl+a': 'selectAll',
  arrowup: 'nudgeUp',
  arrowdown: 'nudgeDown',
  arrowleft: 'nudgeLeft',
  arrowright: 'nudgeRight',
  'shift+arrowup': 'nudgeFastUp',
  'shift+arrowdown': 'nudgeFastDown',
  'shift+arrowleft': 'nudgeFastLeft',
  'shift+arrowright': 'nudgeFastRight',
} as const;

type ShortcutAction = (typeof SHORTCUTS)[keyof typeof SHORTCUTS];

/**
 * Hook for handling keyboard shortcuts in the canvas editor
 */
export function useKeyboardShortcuts() {
  const { canvas, selectedObjects } = useDesignStore();
  const { undo, redo, saveToHistory } = useCanvasStateManager();

  // Use reactive selectors
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  // Debounced save for nudge operations
  const saveNudgeHistory = useMemo(() => {
    return debounce(() => {
      saveToHistory('nudge');
    }, 500);
  }, [saveToHistory]);

  // Build shortcut key from event
  const getShortcutKey = useCallback((e: KeyboardEvent): string => {
    const parts: string[] = [];
    if (e.ctrlKey || e.metaKey) parts.push('ctrl');
    if (e.shiftKey) parts.push('shift');
    if (e.altKey) parts.push('alt');
    parts.push(e.key.toLowerCase());
    return parts.join('+');
  }, []);

  // Execute action
  const executeAction = useCallback(
    async (action: ShortcutAction) => {
      if (!canvas) return;
      // The thread modal is a dialog over the canvas, but the canvas keeps
      // listening: Delete would remove the very motif the dialog is mapping,
      // and Apply would then write a confirmation onto nothing.
      if (useEmbroideryStore.getState().isModalOpen) return;

      switch (action) {
        case 'undo':
          if (canUndo) await undo();
          break;

        case 'redo':
          if (canRedo) await redo();
          break;

        case 'save':
          // Save is handled by CreatorPage to access navigate and design state
          // This event will bubble up and be caught there
          break;

        case 'duplicate':
          if (selectedObjects.length > 0) {
            for (const obj of selectedObjects) {
              const cloned = await obj.clone();
              // Same rule as duplicateSelected in useObjectOperations: a clone
              // that kept the motif key and mapping would give the position two
              // motifs. Stripped, it is an ordinary image, which the publish
              // rule rejects on an embroidery position — the honest outcome.
              if (cloned.embFileKey) {
                cloned.embFileKey = undefined;
                cloned.confirmedMapping = undefined;
              }
              cloned.set({
                left: (cloned.left || 0) + 20,
                top: (cloned.top || 0) + 20,
              });
              canvas.add(cloned);
              canvas.setActiveObject(cloned);
            }
            canvas.requestRenderAll();
            saveToHistory('duplicate');
          }
          break;

        case 'deleteSelected':
          if (selectedObjects.length > 0) {
            selectedObjects.forEach((obj) => canvas.remove(obj));
            canvas.discardActiveObject();
            canvas.requestRenderAll();
            saveToHistory('delete');
          }
          break;

        case 'deselect':
          canvas.discardActiveObject();
          canvas.requestRenderAll();
          break;

        case 'selectAll': {
          canvas.discardActiveObject();
          const objects = canvas
            .getObjects()
            .filter((obj) => obj.selectable !== false);
          if (objects.length > 0) {
            const single = objects[0];
            if (objects.length === 1 && single) {
              canvas.setActiveObject(single);
            } else {
              const selection = new ActiveSelection(objects, { canvas });
              canvas.setActiveObject(selection);
            }
            canvas.requestRenderAll();
          }
          break;
        }

        case 'nudgeUp':
        case 'nudgeDown':
        case 'nudgeLeft':
        case 'nudgeRight':
        case 'nudgeFastUp':
        case 'nudgeFastDown':
        case 'nudgeFastLeft':
        case 'nudgeFastRight': {
          if (selectedObjects.length === 0) return;
          const isFast = action.includes('Fast');
          const delta = isFast ? 10 : 1;
          let dx = 0;
          let dy = 0;

          if (action.includes('Up')) dy = -delta;
          if (action.includes('Down')) dy = delta;
          if (action.includes('Left')) dx = -delta;
          if (action.includes('Right')) dx = delta;

          selectedObjects.forEach((obj) => {
            obj.set({
              left: (obj.left || 0) + dx,
              top: (obj.top || 0) + dy,
            });
            obj.setCoords();
          });
          canvas.requestRenderAll();
          saveNudgeHistory();
          break;
        }
      }
    },
    [
      canvas,
      selectedObjects,
      undo,
      redo,
      canUndo,
      canRedo,
      saveToHistory,
      saveNudgeHistory,
    ]
  );

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input/textarea or editing text on canvas
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Check if we're editing text on canvas
      const activeObject = canvas?.getActiveObject();
      if (
        activeObject &&
        (activeObject.type === 'i-text' || activeObject.type === 'textbox')
      ) {
        const textObj = activeObject as { isEditing?: boolean };
        if (textObj.isEditing) {
          // Only allow undo/redo while editing text
          const shortcutKey = getShortcutKey(e);
          if (
            shortcutKey !== 'ctrl+z' &&
            shortcutKey !== 'ctrl+y' &&
            shortcutKey !== 'ctrl+shift+z'
          ) {
            return;
          }
        }
      }

      const shortcutKey = getShortcutKey(e);
      const action = SHORTCUTS[shortcutKey as keyof typeof SHORTCUTS];

      if (action) {
        e.preventDefault();
        executeAction(action);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [getShortcutKey, executeAction, canvas]);

  return null;
}
